import React, { useEffect, useState } from "react";
import ReleaseDropdown from "./releaseDropdown";
import {
  formatCardDate,
  getReleaseCardFormatLabel,
  getReleaseKindIcon,
  getCardAccentClass,
  getDisplayStatus,
  getReleaseArtworkUrl,
  getStatusBadgeInfo,
  hasQCIssues,
  isCardStatusActionRequired,
  resolveReleaseKind,
} from "./releaseTableHelpers";
import { getDemoTrackBlob, isWlDemoBlobRef, wlDemoBlobStorageKeyFromRef } from "../utils/demoTrackBlobStore";
import { pickArtworkSrc } from "../utils/demoArtworkHelpers";

const ReleaseCardArtwork = ({ row }) => {
  const artworkSrc = pickArtworkSrc(row.artwork) || getReleaseArtworkUrl(row.artwork);
  const [blobSrc, setBlobSrc] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!artworkSrc || !isWlDemoBlobRef(artworkSrc)) {
      setBlobSrc(null);
      setLoadFailed(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;
    setBlobSrc(null);
    setLoadFailed(false);

    (async () => {
      try {
        const storageKey = wlDemoBlobStorageKeyFromRef(artworkSrc);
        const blob = await getDemoTrackBlob(storageKey);
        if (cancelled || !blob) {
          if (!cancelled) {
            setLoadFailed(true);
          }
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setBlobSrc(objectUrl);
      } catch {
        if (!cancelled) {
          setLoadFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [artworkSrc]);

  const resolvedSrc = isWlDemoBlobRef(artworkSrc) ? blobSrc : artworkSrc;

  if (resolvedSrc) {
    return <img className="release-card-artwork-image" src={resolvedSrc} alt={row.name} />;
  }

  return (
    <div className="release-card-artwork-placeholder">
      <i className={`fas ${loadFailed ? "fa-image" : "fa-music"}`} aria-hidden="true" />
    </div>
  );
};

const ReleaseCard = ({
  row,
  userContext,
  tenantContext,
  actionContext,
  buildQuickActions,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setMenuOpen((prev) => !prev);
  };

  const displayStatus = getDisplayStatus(row, userContext);
  const statusContext = {
    release_admin_approval: tenantContext.release_admin_approval,
    is_premium_user: userContext.is_premium_user,
    is_employee_account: userContext.is_employee_account,
  };
  const statusBadge = getStatusBadgeInfo(displayStatus, statusContext);
  const statusActionRequired = isCardStatusActionRequired(displayStatus, statusContext);
  const qcIssues = hasQCIssues(row);
  const accentClass = getCardAccentClass(displayStatus, tenantContext, userContext, qcIssues);
  const releaseKind = resolveReleaseKind(row);
  const formatLabel = getReleaseCardFormatLabel(row);

  const dropdownProps = {
    ...row,
    action: actionContext.dropdownAction,
    todUrl: actionContext.todUrl,
    touUrl: actionContext.touUrl,
    ppUrl: actionContext.ppUrl,
    canAddTakedown: actionContext.canAddTakedown,
    release_admin_approval: tenantContext.release_admin_approval,
    is_premium_user: userContext.is_premium_user,
    is_sub_user: userContext.is_sub_user,
    is_employee_account: userContext.is_employee_account,
    preScanEnabled: tenantContext.acr_deep_scan,
    menuInBody: true,
    hasQcIssues: qcIssues,
    isOpen: menuOpen,
    onToggle: toggleMenu,
  };

  return (
    <article className={`release-card ${accentClass}${qcIssues ? " release-card--has-issue" : ""}`}>
      <div className="release-card-artwork">
        <ReleaseCardArtwork row={row} />
        <span className="release-card-status-badge">
          <span
            className={`status-dot${statusActionRequired ? " status-dot--pulse" : ""}`}
            aria-hidden="true"
          />
          {statusBadge.label}
        </span>
      </div>

      <div className="release-card-body">
        <div className="release-card-header">
          <div className="release-card-titles">
            <h3 className="release-card-title" title={row.name}>{row.name}</h3>
            <p className="release-card-artist" title={row.get_artists}>{row.get_artists}</p>
          </div>
          <button
            type="button"
            className="release-card-menu-btn btn btn-link"
            onClick={toggleMenu}
            aria-label="Release actions"
            aria-expanded={menuOpen}
          >
            <i className="fas fa-ellipsis-v" aria-hidden="true" />
          </button>
        </div>

        <div className="release-card-meta">
          <div className="release-card-meta-item">
            <span className="release-card-meta-label">Cat. No.</span>
            <span className="release-card-meta-value release-card-meta-value--tag">
              {row.catalogue_number || "-"}
            </span>
          </div>
          <div className="release-card-meta-item">
            <span className="release-card-meta-label">Release Date</span>
            <span className="release-card-meta-value">{formatCardDate(row.official_date)}</span>
          </div>
        </div>

        {formatLabel && (
          <div className="release-card-format">
            <span className="release-card-meta-label">Format</span>
            <span className="release-card-format-value">
              <i className={`fas ${getReleaseKindIcon(releaseKind)}`} aria-hidden="true" />
              {formatLabel}
            </span>
          </div>
        )}

        <div className="release-card-footer">
          <div className="release-card-quick-actions" onClick={(e) => e.stopPropagation()}>
            {buildQuickActions(row).map(({ key, content }) => (
              <div key={key} className="release-card-quick-action">
                {content}
              </div>
            ))}
          </div>
          <ReleaseDropdown {...dropdownProps} triggerVariant="cta" />
        </div>
      </div>
    </article>
  );
};

const ReleaseListLayoutTwo = ({
  tableData,
  userContext,
  tenantContext,
  actionContext,
  buildQuickActions,
}) => {
  if (!tableData.length) {
    return (
      <div className="release-card-grid-empty">
        <i className="fas fa-music" aria-hidden="true" />
        <p>No releases found.</p>
      </div>
    );
  }

  return (
    <div className="release-card-grid">
      {tableData.map((row) => (
        <ReleaseCard
          key={row.id}
          row={row}
          userContext={userContext}
          tenantContext={tenantContext}
          actionContext={actionContext}
          buildQuickActions={buildQuickActions}
        />
      ))}
    </div>
  );
};

export default ReleaseListLayoutTwo;
